import Payload from './util/Payload';
import Recording from './util/Recording';
import Browser from './util/Browser';
import queryString from 'query-string';
import ReactDOM from 'react-dom';
import EngagementApp from './components/EngagementApp.react';
import get from 'lodash/get';
import React from 'react';
import { state } from './app';
import { didFinishHide, didFinishLoad, didFinishShow, didFinishStartHide } from './setup-e2';

/**
 * Provides a window.injectOmSdkVerificationScript function for use when omsdk_verify=1
 * in the first action of the payload (the queue_interstitial).
 *
 * NOTE!  UPDATE workbox-config.js when you change versions here or omsdk file structure!
 *
 * @param onAfterLoad A function to run after the omsdk is loaded
 * @returns {boolean} True when the omsdk is supported by the mobile device, false if not
 */
const injectOmSdk = onAfterLoad => {
  const m2mRelsno = Browser.getParameterValue('m2m_relsno');
  const E2Device = m2mRelsno % 2 == 0 ? 'ios' : 'android';

  const validOS =
    (m2mRelsno >= 317 && E2Device === 'android') || (m2mRelsno >= 300 && E2Device === 'ios');

  if (validOS) {
    let doneWithLoad = false;
    const whenDoneWithLoad = () => {
      if (!doneWithLoad) {
        doneWithLoad = true;
        typeof onAfterLoad !== 'function' || onAfterLoad();
      }
    };

    // it's very important that this script is loaded prior to the m2m://interstitial/didFinishPreload call
    // as the client devices will start the omsdk session in that callback...
    // this is one reason why we render the app after the omsdk javascript load
    if (!document.getElementById('omsdk-server-script')) {
      const omsdkJS = document.createElement('script');
      omsdkJS.id = 'omsdk-server-script';
      omsdkJS.type = 'text/javascript';
      omsdkJS.src = '/js/omsdk-js-1.6.7/Service/omsdk-v1.js';
      omsdkJS.onload = whenDoneWithLoad;
      omsdkJS.onerror = whenDoneWithLoad;
      document.head.appendChild(omsdkJS);
    }

    window.injectOmSdkVerificationScript = function () {
      if (!document.getElementById('omsdk-verification-script')) {
        const omsdkVJS = document.createElement('script');
        omsdkVJS.id = 'omsdk-verification-script';
        omsdkVJS.type = 'text/javascript';
        omsdkVJS.src =
          '/js/omsdk-js-1.6.7/Validation-Script/omid-validation-verification-script-v1.js';
        document.head.appendChild(omsdkVJS);
      }
    };
  }
  return validOS;
};

function preload(payload) {
  console.log('>>>>> SDK.preload()');
  console.log('SDK called preload(%@) with payload:', payload); // Match iOS SDK logging format

  // Reset flags for new ad load
  prepareToShowCalled = false;
  showCalled = false;
  actionForCloseInitiatedCalled = false;

  //console.log(`NODE_ENV is ${process.env.NODE_ENV}`);

  if (state.hasInitialized) return false;

  // we're changing the payload format in m2m,
  // this is to ensure that E2 can handle the new format
  let localPayload = Payload.reformat(payload);

  // validate payload format
  localPayload = Payload.validate(localPayload);

  window.server.adsControl.setPayload(localPayload);

  console.log('SDK called preload() with params: ', localPayload);

  // CRITICAL: Set state.serverPayload BEFORE logging so log queue mechanism can work properly
  // log that e2 is loaded
  // perfTime is automatically added by window.server.log()
  state.serverPayload = localPayload;

  // Log SDK → E2 communication AFTER state.serverPayload is set
  // This ensures the log queue mechanism can properly queue/send the log
  // perfTime is automatically added by window.server.log()
  // CRITICAL: Use logForImprIds to ensure log includes impression_id and is queued properly
  try {
    if (window.server && window.server.log && window.server.logForImprIds) {
      // Use logForImprIds pattern (same as E2_LOADED) to ensure:
      // 1. Log includes impression_id from ads
      // 2. Log is queued properly if state.loaded is false
      // 3. Log will be sent when state.loaded becomes true
      window.server.logForImprIds('SDK_CALLBACK', {
        fn: 'window.preload',
        msg: 'SDK called window.preload()',
        sdkCallback: 'preload',
        hasPayload: !!payload,
        payloadKeys: payload ? Object.keys(payload) : [],
        payloadSize: payload ? JSON.stringify(payload).length : 0,
      });

      // Fallback: If logForImprIds doesn't find ads (shouldn't happen, but safety net),
      // log directly without impression_id to ensure we capture the SDK callback
      // Check if logForImprIds actually logged by checking if ads exist
      try {
        let hasAds = false;
        window.server.foreachAd(() => { hasAds = true; });
        if (!hasAds) {
          console.warn('preload() - No ads found, logging SDK_CALLBACK without impression_id');
          window.server.log({
            tag: 'SDK_CALLBACK',
            fn: 'window.preload',
            msg: 'SDK called window.preload() (no ads found)',
            sdkCallback: 'preload',
            hasPayload: !!payload,
            payloadKeys: payload ? Object.keys(payload) : [],
            payloadSize: payload ? JSON.stringify(payload).length : 0,
          });
        }
      } catch (fallbackError) {
        console.error('preload() - Error in fallback log:', fallbackError);
      }
    } else {
      console.error('preload() - CRITICAL: window.server.log not available, log will be missed');
      // Fallback: Try to log after a delay if window.server becomes available
      setTimeout(() => {
        if (window.server && window.server.log) {
          try {
            console.warn('preload() - Retrying log after delay');
            window.server.log({
              tag: 'SDK_CALLBACK',
              fn: 'window.preload',
              msg: 'SDK called window.preload() (delayed log)',
              sdkCallback: 'preload',
              hasPayload: !!payload,
              payloadKeys: payload ? Object.keys(payload) : [],
              payloadSize: payload ? JSON.stringify(payload).length : 0,
            });
          } catch (e) {
            console.error('preload() - Error in delayed log:', e);
          }
        } else {
          console.error('preload() - window.server still not available after delay');
        }
      }, 100);
    }
  } catch (e) {
    console.error('preload() - CRITICAL ERROR logging SDK_CALLBACK:', e);
  }

  window.server.logForImprIds('E2_LOADED');
  if (window.server.adsControl.isFirstAd() && state.serverPayload.data) {
    const tryRecording = (data, check, activate) => {
      try {
        if (check(data)) {
          activate(data);
        }
      } catch (e) {
        console.error('Error', e);
      }
    };
    tryRecording(
      state.serverPayload.data,
      Recording.shouldEnableLogRocket,
      Recording.enableLogRocket
    );
  }

  Object.assign(state, {
    payload: localPayload,
    didFinishHide,
    didFinishStartHide,
    didFinishLoad,
    didFinishShow,
    listeners: {},
    on(event, cb) {
      const l = (state.listeners[event] = state.listeners[event] || []);
      l.push(cb);
    },
    event(event, eventPayload) {
      console.log(`Calling Event ${event}`);
      if (state.listeners[event]) {
        console.log('have listeners for event');
        for (const l of state.listeners[event]) {
          l(eventPayload);
        }
      }
    },
    Browser,
    currentAdIndex: window.server.adsControl.currentAdIndex(),
    adsControl: window.server.adsControl,
  });

  // for the purposes of third-party params, the first url is fine.
  const urls = Payload.findAdUrlsInPayload(localPayload);
  let thirdParams = {};
  if (urls.length > 0) {
    const params = queryString.parseUrl(urls[0]);
    thirdParams = params.query;
    if (thirdParams.url) {
      thirdParams = {
        ...thirdParams,
        ...queryString.parseUrl(thirdParams.url).query,
      };
    }
  }

  // clear any preexisting engagement
  ReactDOM.unmountComponentAtNode(Browser.domContainerNode());
  const renderApp = () => {
    ReactDOM.render(
      <EngagementApp {...state} payload={localPayload} />,
      Browser.domContainerNode(),
      () => {
        // Log that we're notifying SDK of didFinishPreload (iOS SDK logs webAppLoaded=YES)
        // perfTime is automatically added by window.server.log()
        window.server.log({
          tag: 'E2_NOTIFY_SDK',
          fn: 'preload.renderApp',
          msg: 'E2 notifying SDK: didFinishPreload (iOS SDK will set webAppLoaded=YES)',
          sdkCallback: 'didFinishPreload',
        });
        window.server.notifySdk('didFinishPreload');
      }
    );
  };

  // either inject the omsdk then render
  // or just render the app
  window.server.adsControl.isFirstAd() && injectOmSdk(renderApp) || renderApp();

  return '';
}

function doHeartBeat() {
  // NOTE: We intentionally do NOT log doHeartBeat calls because:
  // 1. Heartbeat is called every ~2 seconds, creating excessive log volume
  // 2. Heartbeat logs are not critical for debugging SDK-E2 communication
  // 3. Logging every heartbeat causes significant performance overhead
  // If heartbeat debugging is needed, it can be enabled via a feature flag

  // Best-effort unload attribution marker (no server log)
  markUnloadAttribution('SDK_DO_HEARTBEAT', { os: detectOsForAttribution() });
  // For watchdog: record that we *did* see a heartbeat reach JS.
  lastHeartbeatAtMs = Date.now();

  if (get(window, 'M2MWebView.onHeartBeat')) {
    // Android
    window.M2MWebView.onHeartBeat();
  } else if (get(window, 'webkit.messageHandlers.onHeartBeat')) {
    // iOS
    window.webkit.messageHandlers.m2mJsHandler.onHeartBeat();
  } else {
    console.warn(`SDK onHeartBeat isn't defined on the client side`);
  }

  return '';
}

/**
 * Should only be called from within E2 manually:
 * - for non-mobile browsers, or
 * - on a manual payload inject.
 *
 * Invoked by the Android SDK as soon as possible after the `m2m://interstitial/didFinishPreload` call.
 * Invoked by the iOS SDK as soon as possible after the `m2m://interstitial/didFinishLoad` call.
 * It is invoked in both cases only at the point the app is in the foreground or coming into the foreground.
 *
 * Given the deviation, the earliest vector needs to be maintained to prevent session loss.
 *
 * The `m2m://interstitial/didFinishPrepareToShow`, called herein, is effectively a boomerang back
 * into the platform sdk that then triggers the tracking sessions for any platform libraries we
 * integrate with (ie, the IAB OM SDK).
 *
 * For the IAB OM SDK, it is imperative that the Service javascript be loaded prior to the platform sdk
 * calling this method.
 *
 * Within a line or two of the sdk calling this method, the Android sdk notifies publisher M2M listeners
 * that an engagement is being viewed.
 *
 * The iOS platform SDK notifies M2M delegates that an engagement is being viewed in response to a
 * notification that is sent when the M2MViewController is presented.
 *
 * @returns {string}
 */
function prepareToShow() {
  // Guard against duplicate calls
  if (prepareToShowCalled) {
    console.warn('>>>>> SDK.prepareToShow() - already called, skipping duplicate');
    return '';
  }
  prepareToShowCalled = true;

  // establish perforamnce point in time to reference for E2 execution time
  window.displayPerfPoint =
    window.displayPerfPoint ? window.displayPerfPoint : performance.now();
  console.log('>>>>> SDK.prepareToShow()');

  // Log SDK → E2 communication
  // perfTime is automatically added by window.server.log()
  if (window.server && window.server.foreachAd) {
    window.server.foreachAd(ad => {
      window.server.log({
        tag: 'SDK_CALLBACK',
        fn: 'window.prepareToShow',
        msg: 'SDK called window.prepareToShow()',
        sdkCallback: 'prepareToShow',
        inmarketParams: {
          m2m_impr_id: ad.impression_id,
        },
      });
    });
  } else {
    // perfTime is automatically added by window.server.log()
    window.server.logForImprIds('SDK_CALLBACK_PREPARE_TO_SHOW');
  }

  // logging to server
  // perfTime is automatically added by window.server.log()
  window.server.logForImprIds('E2_PREPARE_TO_SHOW');

  /**
   * Immediately in response to this call, the both platforms start
   * any tracking sessions that they will start with native libraries.
   * (ie, IAB OM SDK)
   */
  window.server.notifySdk('didFinishPrepareToShow');
  return '';
}

function logRequestUrl(url) {
  console.log('>>>>> SDK.logRequetUrl()');

  // Log SDK → E2 communication
  // perfTime is automatically added by window.server.log()
  if (window.server && window.server.foreachAd) {
    window.server.foreachAd(ad => {
      window.server.log({
        tag: 'SDK_CALLBACK',
        fn: 'window.logRequestUrl',
        msg: 'SDK called window.logRequestUrl()',
        sdkCallback: 'logRequestUrl',
        url: url,
        inmarketParams: {
          m2m_impr_id: ad.impression_id,
        },
      });
    });
  }

  window.server.logRequestUrl(url);
  // another voodoo moment here for android
  // we need to figure out a way to isolate what exactly causes this
  // to be required... it's likely linked to periodic disappearances of the ad
  const parsed = queryString.parseUrl(window.location.href);
  const relsNo = get(parsed, 'query.m2m_relsno');
  if (relsNo % 2 === 1) {
    window.server.notifySdk('ignoreThis');
  }
  return '';
}

function show() {
  // Guard against duplicate calls
  if (showCalled) {
    console.warn('>>>>> SDK.show() - already called, skipping duplicate');
    return '';
  }
  showCalled = true;

  console.log('>>>>> SDK.show()');

  // Log SDK → E2 communication
  // perfTime is automatically added by window.server.log()
  if (window.server && window.server.foreachAd) {
    window.server.foreachAd(ad => {
      window.server.log({
        tag: 'SDK_CALLBACK',
        fn: 'window.show',
        msg: 'SDK called window.show()',
        sdkCallback: 'show',
        inmarketParams: {
          m2m_impr_id: ad.impression_id,
        },
      });
    });
  } else {
    // perfTime is automatically added by window.server.log()
    window.server.logForImprIds('SDK_CALLBACK_SHOW');
  }

  // logging to server
  // perfTime is automatically added by window.server.log()
  window.server.logForImprIds('E2_SHOW');

  // Heartbeat watchdog (best-effort):
  // If we stop seeing doHeartBeat() calls while visible, it’s a strong signal of JS eval problems / WK issues.
  try {
    // Only enable watchdog when server logging is enabled; otherwise it provides no value and adds noise.
    if (!(Browser.shouldSendEngagementLog && Browser.shouldSendEngagementLog())) {
      // reset state, ensure nothing is running
      lastHeartbeatAtMs = null;
      heartbeatMissLogged = false;
      if (heartbeatWatchdogInterval) {
        clearInterval(heartbeatWatchdogInterval);
        heartbeatWatchdogInterval = null;
      }
      // continue normal flow
    } else {
      // reset per-ad/session state
      lastHeartbeatAtMs = Date.now();
      heartbeatMissLogged = false;
      if (heartbeatWatchdogInterval) {
        clearInterval(heartbeatWatchdogInterval);
        heartbeatWatchdogInterval = null;
      }
      heartbeatWatchdogInterval = setInterval(() => {
        try {
          if (heartbeatMissLogged) return;
          // Avoid false positives when hidden/backgrounded.
          if (document.visibilityState && document.visibilityState !== 'visible') return;

          const nowMs = Date.now();
          if (lastHeartbeatAtMs === null) return;

          // SDK heartbeat is expected roughly every ~2s; use a conservative threshold to avoid noise.
          const ageMs = nowMs - lastHeartbeatAtMs;
          if (ageMs >= 6000) {
            heartbeatMissLogged = true;
            safeLog({
              tag: 'E2_HEARTBEAT_MISSED',
              fn: 'heartbeatWatchdog',
              msg: 'No doHeartBeat() observed for >= 6000ms while visible (best-effort heartbeat failure signal)',
              diagnostic: {
                os: detectOsForAttribution(),
                heartbeatAgeMs: ageMs,
                documentVisibilityState: (() => {
                  try { return document.visibilityState; } catch (e) { return 'unknown'; }
                })(),
                windowFocused: (() => {
                  try { return document.hasFocus ? document.hasFocus() : 'unknown'; } catch (e) { return 'unknown'; }
                })(),
              },
            });
          }
        } catch (e) {
          // no-op
        }
      }, 1000);
    }
  } catch (e) {
    // no-op
  }

  if (!state.hasInitialized) {
    state.hasInitialized = true;
    console.log('calling app opened');

    if (get(state, 'event')) {
      state.event('app_opened');
    }
    if (!document.hasFocus()) {
      window.focus();
    }
  }
  return '';
}

// CRITICAL FIX: Module-level flags to prevent duplicate SDK function calls
// This prevents the iOS SDK race condition where ViewController is dismissed
// but cleanup never happens if JavaScript execution fails
let didFinishUnloadSent = false;
let prepareToShowCalled = false;
let showCalled = false;
let actionForCloseInitiatedCalled = false;

// Heartbeat watchdog (best-effort inference when native closes due to heartbeat/js eval failures)
let lastHeartbeatAtMs = null;
let heartbeatMissLogged = false;
let heartbeatWatchdogInterval = null;

// SAFE LOGGING HELPER: Wraps logging in try-catch to prevent breaking the flow
const safeLog = (logData) => {
  let count = 0;
  try {
    if (window.server && window.server.foreachAd) {
      window.server.foreachAd(ad => {
        try {
          window.server.log({
            ...logData,
            inmarketParams: {
              m2m_impr_id: ad.impression_id,
            },
          });
          count += 1;
        } catch (logError) {
          // Logging failed - don't break the flow, just log to console
          console.warn('safeLog - Failed to log diagnostic:', logError);
        }
      });
    }
  } catch (e) {
    // Even foreachAd failed - don't break the flow
    console.warn('safeLog - Failed to iterate ads:', e);
  }
  return count;
};

// For unload-critical diagnostics, safeLog can drop rows when foreachAd yields nothing.
// This helper emits a best-effort fallback log without inmarketParams in that case.
const safeLogWithFallback = (logData) => {
  const count = safeLog(logData);
  if (count > 0) return count;
  try {
    if (window.server && typeof window.server.log === 'function') {
      window.server.log({
        ...logData,
        diagnostic: {
          ...(logData && logData.diagnostic ? logData.diagnostic : {}),
          noImpressionId: true,
        },
      });
      return 1;
    }
  } catch (e) {
    // no-op
  }
  return 0;
};

// SAFE DIAGNOSTIC HELPER: Safely captures diagnostic info without throwing errors
const safeGetDiagnosticInfo = () => {
  try {
    return {
      timestamp: new Date().toISOString(),
      perfTime: typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null,
      hasWindowServer: !!(window.server),
      hasNotifySdk: !!(window.server && window.server.notifySdk),
      hasState: !!(state),
      hasStateEvent: !!(get(state, 'event')),
      hasInitialized: get(state, 'hasInitialized', false),
      hasListeners: !!(get(state, 'listeners')),
      domContainerExists: !!(Browser.domContainerNode()),
      documentReadyState: (() => {
        try { return document.readyState; } catch (e) { return 'unknown'; }
      })(),
      documentVisibilityState: (() => {
        try { return document.visibilityState; } catch (e) { return 'unknown'; }
      })(),
      windowFocused: (() => {
        try { return document.hasFocus ? document.hasFocus() : 'unknown'; } catch (e) { return 'unknown'; }
      })(),
    };
  } catch (e) {
    // If diagnostic creation fails, return minimal info
    console.warn('safeGetDiagnosticInfo - Failed to create diagnostic:', e);
    return {
      timestamp: new Date().toISOString(),
      perfTime: typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null,
      diagnosticError: e.toString(),
    };
  }
};

// -----------------------------------------------------------------------------
// Unload attribution (best-effort, no SDK changes)
// -----------------------------------------------------------------------------
// Native SDK calls `unload()` without arguments, so we can't *guarantee* source.
// We correlate against recent events we can observe and log:
// - OS (iOS/Android/Unknown)
// - reason (human-readable)
// - confidence (HIGH/MEDIUM/LOW/UNKNOWN)
const getUnloadAttributionStore = () => {
  try {
    if (!window.__e2UnloadAttribution) {
      window.__e2UnloadAttribution = { version: 1, events: {} };
    }
    return window.__e2UnloadAttribution;
  } catch (e) {
    return null;
  }
};

const markUnloadAttribution = (eventName, details = {}) => {
  try {
    const store = getUnloadAttributionStore();
    if (!store || !store.events) return;
    const nowMs = Date.now();
    store.events[eventName] = {
      atMs: nowMs,
      atIso: new Date(nowMs).toISOString(),
      ...details,
    };
  } catch (e) {
    // Never break flow
  }
};

const getUnloadAttributionEvent = (eventName) => {
  try {
    const store = getUnloadAttributionStore();
    return store && store.events ? store.events[eventName] : null;
  } catch (e) {
    return null;
  }
};

const detectOsForAttribution = () => {
  try {
    // In mock-m2m (desktop browser) we can still label the *intended* platform
    // using m2m_relsno parity (same logic used by injectOmSdk()).
    if (Browser.useMockM2M && Browser.useMockM2M()) {
      const relsNoStr = Browser.getParameterValue ? Browser.getParameterValue('m2m_relsno') : null;
      const relsNo = relsNoStr ? parseInt(relsNoStr, 10) : NaN;
      if (!Number.isNaN(relsNo)) {
        return relsNo % 2 === 0 ? 'Mock-iOS' : 'Mock-Android';
      }
      return 'Mock';
    }
    // Prefer explicit runtime signals first
    if (Browser.isIOS()) return 'iOS';
    if (get(window, 'webkit.messageHandlers')) return 'iOS';
    if (get(window, 'M2MWebView')) return 'Android';

    // Fallback to UA sniffing (covers Android WebView cases where no JS bridge is injected)
    const ua = (get(window, 'navigator.userAgent') || '').toString();
    if (/Android/i.test(ua)) return 'Android';
    if (/(iPhone|iPad|iPod)/i.test(ua)) return 'iOS';

    return 'Unknown';
  } catch (e) {
    return 'Unknown';
  }
};

const computeUnloadReason = () => {
  const os = detectOsForAttribution();
  const nowMs = Date.now();
  const ageMs = (evt) => (evt && typeof evt.atMs === 'number' ? nowMs - evt.atMs : null);

  const didFinishHideAge = ageMs(getUnloadAttributionEvent('E2_DID_FINISH_HIDE'));
  const didFinishStartHideAge = ageMs(getUnloadAttributionEvent('E2_DID_FINISH_START_HIDE'));
  const userCloseClickAge = ageMs(getUnloadAttributionEvent('E2_USER_CLOSE_CLICK'));
  const iframeDismissAge = ageMs(getUnloadAttributionEvent('E2_IFRAME_DISMISS'));
  const sdkActionForCloseInitiatedAge = ageMs(getUnloadAttributionEvent('SDK_ACTION_FOR_CLOSE_INITIATED'));
  const sdkDismissCalledAge = ageMs(getUnloadAttributionEvent('SDK_DISMISS_CALLED'));
  const heartbeatAge = ageMs(getUnloadAttributionEvent('SDK_DO_HEARTBEAT'));

  // Always include full age snapshot for debugging/testing.
  const allAgesMs = {
    didFinishHideAge,
    didFinishStartHideAge,
    userCloseClickAge,
    iframeDismissAge,
    sdkActionForCloseInitiatedAge,
    sdkDismissCalledAge,
    heartbeatAge,
  };

  // HIGH: unload shortly after didFinishHide (normal flow: didFinishHide -> SDK -> unload)
  if (didFinishHideAge !== null && didFinishHideAge >= 0 && didFinishHideAge <= 10000) {
    return {
      os,
      reason: `${os}: expected unload after didFinishHide (normal dismissal flow)`,
      confidence: 'HIGH',
      reasonCode: 'DID_FINISH_HIDE_RECENT',
      agesMs: allAgesMs,
    };
  }

  // HIGH (iOS): unload shortly after SDK actionForCloseInitiated (SDK-initiated close)
  if (os === 'iOS' && sdkActionForCloseInitiatedAge !== null && sdkActionForCloseInitiatedAge >= 0 && sdkActionForCloseInitiatedAge <= 15000) {
    return {
      os,
      reason: 'iOS: expected unload after SDK actionForCloseInitiated (SDK-initiated close)',
      confidence: 'HIGH',
      reasonCode: 'SDK_ACTION_FOR_CLOSE_INITIATED_RECENT',
      agesMs: allAgesMs,
    };
  }

  // MEDIUM: user close click observed but didFinishHide not observed
  if (userCloseClickAge !== null && userCloseClickAge >= 0 && userCloseClickAge <= 10000) {
    return {
      os,
      reason: `${os}: likely unload after user close click (didFinishHide not observed)`,
      confidence: 'MEDIUM',
      reasonCode: 'USER_CLOSE_CLICK_RECENT',
      agesMs: allAgesMs,
    };
  }

  // MEDIUM: iframe dismiss requested
  if (iframeDismissAge !== null && iframeDismissAge >= 0 && iframeDismissAge <= 10000) {
    return {
      os,
      reason: `${os}: likely unload after iframe dismiss request`,
      confidence: 'MEDIUM',
      reasonCode: 'IFRAME_DISMISS_RECENT',
      agesMs: allAgesMs,
    };
  }

  // MEDIUM (iOS OM flow): didFinishStartHide observed; timing varies
  if (os === 'iOS' && didFinishStartHideAge !== null && didFinishStartHideAge >= 0 && didFinishStartHideAge <= 20000) {
    return {
      os,
      reason: 'iOS: possible unload related to OM flow (didFinishStartHide observed)',
      confidence: 'MEDIUM',
      reasonCode: 'DID_FINISH_START_HIDE_RECENT',
      agesMs: allAgesMs,
    };
  }

  // LOW (iOS): heartbeat observed very recently; SDK dismisses on heartbeat JS eval failure
  if (os === 'iOS' && heartbeatAge !== null && heartbeatAge >= 0 && heartbeatAge <= 2500) {
    return {
      os,
      reason: 'iOS: suspected unload due to heartbeat JS eval failure (best-effort inference)',
      confidence: 'LOW',
      reasonCode: 'HEARTBEAT_RECENT',
      agesMs: allAgesMs,
    };
  }

  // LOW: SDK dismiss() recently invoked (Android back button triggers window.dismiss)
  if (sdkDismissCalledAge !== null && sdkDismissCalledAge >= 0 && sdkDismissCalledAge <= 15000) {
    return {
      os,
      reason: `${os}: possible unload related to SDK dismiss() flow`,
      confidence: 'LOW',
      reasonCode: 'SDK_DISMISS_RECENT',
      agesMs: allAgesMs,
    };
  }

  return {
    os,
    reason: `${os}: UNKNOWN`,
    confidence: 'UNKNOWN',
    reasonCode: 'UNKNOWN',
    agesMs: allAgesMs,
  };
};

const runUnload = () => {
  console.log('>>>>> SDK.runUnload()');

  // DIAGNOSTIC: Safely capture app state at start of runUnload
  const diagnosticInfo = safeGetDiagnosticInfo();

  const sendDidFinishUnload = () => {
    if (didFinishUnloadSent) {
      console.warn('runUnload - didFinishUnload already sent, skipping duplicate');
      // Log diagnostic even for duplicate (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'runUnload.sendDidFinishUnload',
        msg: 'didFinishUnload already sent (duplicate call prevented)',
        diagnostic: diagnosticInfo,
      });
      return;
    }
    didFinishUnloadSent = true;
    try {
      window.server.notifySdk('didFinishUnload');
      console.log('runUnload - didFinishUnload sent successfully');

      // Log successful send with diagnostics (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'runUnload.sendDidFinishUnload',
        msg: 'didFinishUnload sent successfully',
        diagnostic: diagnosticInfo,
      });
    } catch (e) {
      console.error('runUnload - CRITICAL: Failed to send didFinishUnload:', e);

      // Log failure with detailed diagnostics (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'runUnload.sendDidFinishUnload',
        msg: 'Failed to send didFinishUnload (first attempt)',
        error: e.toString(),
        errorMessage: e.message,
        errorStack: e.stack,
        diagnostic: diagnosticInfo,
      });

      // Try one more time with minimal code
      try {
        if (window.server && window.server.notifySdk) {
          window.server.notifySdk('didFinishUnload');

          // Log successful retry (non-critical, safe to fail)
          safeLog({
            tag: 'E2_UNLOAD_DIAGNOSTIC',
            fn: 'runUnload.sendDidFinishUnload',
            msg: 'didFinishUnload sent successfully (retry)',
            diagnostic: diagnosticInfo,
          });
        }
      } catch (e2) {
        console.error('runUnload - CRITICAL: Second attempt to send didFinishUnload also failed:', e2);

        // Log second failure (non-critical, safe to fail)
        safeLog({
          tag: 'E2_UNLOAD_DIAGNOSTIC',
          fn: 'runUnload.sendDidFinishUnload',
          msg: 'Failed to send didFinishUnload (second attempt also failed)',
          error: e2.toString(),
          errorMessage: e2.message,
          errorStack: e2.stack,
          firstError: e.toString(),
          diagnostic: diagnosticInfo,
        });
      }
    }
  };

  try {
    // Log internal runUnload execution (NOT an SDK callback - this is called internally by unload())
    // perfTime is automatically added by window.server.log()
    safeLog({
      tag: 'E2_INTERNAL',
      fn: 'runUnload',
      msg: 'runUnload() executing (internal call from unload(), not SDK callback)',
      diagnostic: diagnosticInfo,
    });

    if (get(state, 'event')) {
      state.event('unload');
    }

    // Try to unmount React component
    let unmountResult = false;
    let unmountError = null;
    try {
      const containerNode = Browser.domContainerNode();
      if (containerNode) {
        unmountResult = ReactDOM.unmountComponentAtNode(containerNode);
        if (unmountResult) {
          console.log('successfully unloaded application()');
        } else {
          console.log('did not unload application (may already be unmounted)');
        }
      } else {
        console.warn('runUnload - domContainerNode is null/undefined');
      }
    } catch (err) {
      unmountError = err;
      console.error('runUnload - Error during React unmount:', err);

      // Log unmount error with diagnostics (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'runUnload.unmountComponent',
        msg: 'Error during React component unmount',
        error: err.toString(),
        errorMessage: err.message,
        errorStack: err.stack,
        diagnostic: diagnosticInfo,
      });
      // Continue - unmounting failure shouldn't prevent didFinishUnload
    }

    // Cleanup state
    let stateCleanupError = null;
    try {
      state.listeners = {};
      state.hasInitialized = false;
    } catch (err) {
      stateCleanupError = err;
      console.error('runUnload - Error cleaning up state:', err);

      // Log state cleanup error with diagnostics (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'runUnload.stateCleanup',
        msg: 'Error during state cleanup',
        error: err.toString(),
        errorMessage: err.message,
        errorStack: err.stack,
        diagnostic: diagnosticInfo,
      });
      // Continue - state cleanup failure shouldn't prevent didFinishUnload
    }

    // Log completion status (non-critical, safe to fail)
    safeLog({
      tag: 'E2_UNLOAD_DIAGNOSTIC',
      fn: 'runUnload.completion',
      msg: 'runUnload completed',
      unmountResult: unmountResult,
      hadUnmountError: !!unmountError,
      hadStateCleanupError: !!stateCleanupError,
      diagnostic: diagnosticInfo,
    });

    // CRITICAL: Always send didFinishUnload, even if previous steps failed
    sendDidFinishUnload();

  } catch (error) {
    // CRITICAL: If ANY error occurs, still send didFinishUnload
    // This ensures iOS SDK can cleanup even if React app fails
    console.error('runUnload - CRITICAL ERROR:', error);

    // Log critical error with full diagnostics (non-critical, safe to fail)
    safeLog({
      tag: 'E2_UNLOAD_DIAGNOSTIC',
      fn: 'runUnload.criticalError',
      msg: 'CRITICAL ERROR in runUnload - top-level catch',
      error: error.toString(),
      errorMessage: error.message,
      errorStack: error.stack,
      errorName: error.name,
      diagnostic: diagnosticInfo,
    });

    sendDidFinishUnload();
  }
};

function unload() {
  // DIAGNOSTIC: Safely capture app state when unload() is called
  const unloadStartTime = typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null;
  let unloadReason = computeUnloadReason();

  // Defensive fix: if the SDK calls unload() before show() has ever been called for this session,
  // do NOT honor it (prevents premature UI teardown that can leave an invisible WebView in front).
  // iOS-only (including Mock-iOS) because the stuck-foreground symptom is iOS-specific.
  const isIosLike = Browser.isIOS() || unloadReason.os === 'iOS' || unloadReason.os === 'Mock-iOS';
  const shouldIgnorePreShowUnload = isIosLike && !showCalled;
  if (shouldIgnorePreShowUnload) {
    unloadReason = {
      os: unloadReason.os,
      reason: `${unloadReason.os}: SDK called unload() before show(); ignoring to prevent premature dismissal`,
      confidence: 'HIGH',
      reasonCode: 'UNLOAD_BEFORE_SHOW_IGNORED',
      agesMs: unloadReason.agesMs,
    };
  }

  // DEV/TEST: In mock-m2m runs, also emit to console so Puppeteer/local testing can assert attribution.
  // This does NOT run in production (where Browser.useMockM2M() is false).
  try {
    if (Browser.useMockM2M && Browser.useMockM2M()) {
      console.warn('E2_UNLOAD_ATTRIBUTION', JSON.stringify(unloadReason));
    }
  } catch (e) {
    // no-op
  }

  const unloadDiagnosticInfo = (() => {
    try {
      return {
        timestamp: new Date().toISOString(),
        perfTime: unloadStartTime,
        os: unloadReason.os,
        unloadReason: unloadReason.reason,
        unloadConfidence: unloadReason.confidence,
        unloadReasonCode: unloadReason.reasonCode,
        unloadReasonAgesMs: unloadReason.agesMs,
        showCalled: showCalled,
        prepareToShowCalled: prepareToShowCalled,
        shouldIgnorePreShowUnload: shouldIgnorePreShowUnload,
        hasWindowServer: !!(window.server),
        hasAdsControl: !!(window.server && window.server.adsControl),
        hasNextAd: !!(window.server && window.server.adsControl && window.server.adsControl.nextAd),
        isIOS: Browser.isIOS(),
        documentReadyState: (() => {
          try { return document.readyState; } catch (e) { return 'unknown'; }
        })(),
        documentVisibilityState: (() => {
          try { return document.visibilityState; } catch (e) { return 'unknown'; }
        })(),
        windowFocused: (() => {
          try { return document.hasFocus ? document.hasFocus() : 'unknown'; } catch (e) { return 'unknown'; }
        })(),
        didFinishUnloadAlreadySent: didFinishUnloadSent,
      };
    } catch (e) {
      console.warn('unload - Failed to create diagnostic info:', e);
      return {
        timestamp: new Date().toISOString(),
        perfTime: unloadStartTime,
        os: unloadReason.os,
        unloadReason: unloadReason.reason,
        unloadConfidence: unloadReason.confidence,
        unloadReasonCode: unloadReason.reasonCode,
        unloadReasonAgesMs: unloadReason.agesMs,
        diagnosticError: e.toString(),
      };
    }
  })();

  // Log SDK → E2 communication with diagnostics (non-critical, safe to fail)
  // perfTime is automatically added by window.server.log()
  const unloadSdkCallbackLog = {
    tag: 'SDK_CALLBACK',
    fn: 'window.unload',
    msg: `SDK called window.unload() | ${unloadReason.os} | ${unloadReason.confidence} | ${unloadReason.reason}`,
    sdkCallback: 'unload',
    unloadReason: unloadReason.reason,
    unloadConfidence: unloadReason.confidence,
    unloadReasonCode: unloadReason.reasonCode,
    diagnostic: unloadDiagnosticInfo,
  };
  const unloadLogCount = safeLog(unloadSdkCallbackLog);

  // IMPORTANT: If unload() happens before we have impression ids (e.g. SDK early close),
  // foreachAd yields nothing and safeLog would drop this event entirely.
  // In that case, log once without inmarketParams so we still have a forensic record.
  if (unloadLogCount === 0) {
    try {
      if (window.server && typeof window.server.log === 'function') {
        window.server.log({
          ...unloadSdkCallbackLog,
          msg: `${unloadSdkCallbackLog.msg} (no impression_id available yet)`,
          diagnostic: {
            ...unloadDiagnosticInfo,
            noImpressionId: true,
          },
        });
      }
    } catch (e) {
      // no-op
    }
  }

  // If we decided to ignore this unload, record diagnostics and return without tearing down the app.
  if (shouldIgnorePreShowUnload) {
    safeLogWithFallback({
      tag: 'E2_UNLOAD_DIAGNOSTIC',
      fn: 'unload.ignoredBeforeShow',
      msg: 'Ignoring window.unload() because show() has not been called yet (pre-show unload)',
      diagnostic: unloadDiagnosticInfo,
    });
    return '';
  }

  // skip any further communication with the sdk till after we cycle through the ads.
  if (window.server.adsControl.nextAd()) {
    // Log that we're skipping due to next ad (non-critical, safe to fail)
    safeLog({
      tag: 'E2_UNLOAD_DIAGNOSTIC',
      fn: 'unload.nextAd',
      msg: 'Skipping unload - next ad exists',
      diagnostic: unloadDiagnosticInfo,
    });
    return;
  }

  // CRITICAL FIX: Track timeout to ensure didFinishUnload is sent even if runUnload fails
  // Uses module-level didFinishUnloadSent flag to prevent duplicates
  let didFinishUnloadTimeout = null;

  // Stop heartbeat watchdog as soon as we enter unload()
  try {
    if (heartbeatWatchdogInterval) {
      clearInterval(heartbeatWatchdogInterval);
      heartbeatWatchdogInterval = null;
    }
  } catch (e) {
    // no-op
  }

  const ensureDidFinishUnload = () => {
    if (didFinishUnloadSent) {
      // Log duplicate prevention (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'unload.ensureDidFinishUnload',
        msg: 'didFinishUnload already sent (fallback duplicate prevented)',
        diagnostic: unloadDiagnosticInfo,
      });
      return;
    }
    didFinishUnloadSent = true;
    if (didFinishUnloadTimeout) {
      clearTimeout(didFinishUnloadTimeout);
      didFinishUnloadTimeout = null;
    }
    try {
      window.server.notifySdk('didFinishUnload');
      console.log('unload - didFinishUnload sent (fallback)');

      // Log successful fallback send (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'unload.ensureDidFinishUnload',
        msg: 'didFinishUnload sent successfully (fallback)',
        diagnostic: unloadDiagnosticInfo,
      });
    } catch (e) {
      console.error('unload - CRITICAL: Failed to send didFinishUnload fallback:', e);

      // Log fallback failure (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'unload.ensureDidFinishUnload',
        msg: 'CRITICAL: Failed to send didFinishUnload fallback',
        error: e.toString(),
        errorMessage: e.message,
        errorStack: e.stack,
        diagnostic: unloadDiagnosticInfo,
      });
    }
  };

  if (Browser.isIOS()) {
    /*
     "Note that ending an OMID ad session sends a message to the verification scripts running inside the
     webview supplied by the integration. So that the verification scripts have enough time to handle the
     sessionFinish event, the integration must maintain a strong reference to the webview for at least 1.0
     seconds after ending the session."
     - https://interactiveadvertisingbureau.github.io/Open-Measurement-SDKiOS/#7-stop-the-session
     - https://interactiveadvertisingbureau.github.io/Open-Measurement-SDKAndroid/#8-stop-the-session

     The actual window doesn't get hidden/destroyed till unload() responds with `m2m://interstitial/didFinishUnload`,
     therefore at this vector we should install, for iOS only, a 1.5s to allow OM SDK to finish the session,
     which would have been triggered in `m2m://interstitial/didFinishHide`
     (cond calls [M2M_WebView closeButtonPressed] in iOS, which will then call window.unload())
     X -> didFinishHide(finish om) -> unload() -(our delay here)-> didFinishUnload

     For Android our case is currently covered in the platform code and this above `isIOS` condition should eventually
     include a version check, as it should be covered in the platform there as well.

     This timeout was specifically introduced because we were not consistently seeing the `sessionFinish` event through
     Charles Proxy on iOS from the OM SDK validation script, provided by IAB.
    */

    // CRITICAL FIX: Add timeout fallback to ensure didFinishUnload is sent even if runUnload fails
    // This prevents the race condition where ViewController is dismissed but cleanup never happens
    // Set timeout to 2 seconds (1.5s OM SDK delay + 0.5s buffer)
    didFinishUnloadTimeout = setTimeout(() => {
      const timeoutTime = typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null;
      console.warn('unload - TIMEOUT: runUnload did not complete within 2 seconds, sending didFinishUnload fallback');

      // Log timeout with diagnostics (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'unload.timeoutFallback',
        msg: 'TIMEOUT: runUnload did not complete within 2 seconds - using fallback',
        timeoutTime: timeoutTime,
        timeSinceUnloadCalled: timeoutTime && unloadStartTime ? (timeoutTime - unloadStartTime).toFixed(2) : null,
        diagnostic: unloadDiagnosticInfo,
      });

      ensureDidFinishUnload();
    }, 2000);

    setTimeout(() => {
      const runUnloadStartTime = typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null;

      // Log that runUnload is about to execute (non-critical, safe to fail)
      safeLog({
        tag: 'E2_UNLOAD_DIAGNOSTIC',
        fn: 'unload.runUnloadScheduled',
        msg: 'runUnload scheduled to execute (after 1.5s delay)',
        runUnloadStartTime: runUnloadStartTime,
        timeSinceUnloadCalled: runUnloadStartTime && unloadStartTime ? (runUnloadStartTime - unloadStartTime).toFixed(2) : null,
        diagnostic: unloadDiagnosticInfo,
      });

      try {
        runUnload();
        // Clear timeout if runUnload completes successfully
        if (didFinishUnloadTimeout) {
          clearTimeout(didFinishUnloadTimeout);
          didFinishUnloadTimeout = null;

          // Log successful completion (non-critical, safe to fail)
          const completionTime = typeof performance !== 'undefined' && performance.now ? Math.round(performance.now() * 100) / 100 : null;
          safeLog({
            tag: 'E2_UNLOAD_DIAGNOSTIC',
            fn: 'unload.runUnloadCompleted',
            msg: 'runUnload completed successfully - timeout cleared',
            completionTime: completionTime,
            timeSinceScheduled: completionTime && runUnloadStartTime ? (completionTime - runUnloadStartTime).toFixed(2) : null,
            diagnostic: unloadDiagnosticInfo,
          });
        }
      } catch (error) {
        console.error('unload - CRITICAL: runUnload threw error:', error);

        // Log error with diagnostics (non-critical, safe to fail)
        safeLog({
          tag: 'E2_UNLOAD_DIAGNOSTIC',
          fn: 'unload.runUnloadError',
          msg: 'CRITICAL: runUnload threw error in setTimeout callback',
          error: error.toString(),
          errorMessage: error.message,
          errorStack: error.stack,
          errorName: error.name,
          runUnloadStartTime: runUnloadStartTime,
          timeSinceUnloadCalled: runUnloadStartTime && unloadStartTime ? (runUnloadStartTime - unloadStartTime).toFixed(2) : null,
          diagnostic: unloadDiagnosticInfo,
        });

        // Ensure didFinishUnload is sent even if runUnload fails
        ensureDidFinishUnload();
      }
    }, 1500);
  } else {
    try {
      runUnload();
    } catch (error) {
      console.error('unload - CRITICAL: runUnload threw error:', error);
      // Ensure didFinishUnload is sent even if runUnload fails
      ensureDidFinishUnload();
    }
  }
  return '';
}

/**
 * If an http(s) request is made by the WebApp but the WebApp is not able
 * to intercept and handle it.
 *
 * In Android, there is an interceptor that catches navigation to http(s): urls,
 * and posts back here with the url.  We then just launch that url in the browser.
 * For this reason, E2 cannot be reloaded using window.document.location.reload,
 * however, it is safe for any arbitrary creative to perform navigation and that
 * will safely wind up in an external browser instance.
 */
function willSendRequestWithURL(url) {
  // Log SDK → E2 communication
  // perfTime is automatically added by window.server.log()
  if (window.server && window.server.foreachAd) {
    window.server.foreachAd(ad => {
      window.server.log({
        tag: 'SDK_CALLBACK',
        fn: 'window.willSendRequestWithURL',
        msg: 'SDK called window.willSendRequestWithURL()',
        sdkCallback: 'willSendRequestWithURL',
        url: url,
        inmarketParams: {
          m2m_impr_id: ad.impression_id,
        },
      });
    });
  }

  if (url.startsWith(document.location.href)) {
    console.log('SDK called willSendRequestWithURL(), but it is here so not doing anything');
    return;
  }

  const localUrl = url;
  try {
    console.log('SDK called willSendRequestWithURL()');
  } catch (e) {
    console.log('SDK called willSendRequestWithURL() error', e.message);
  }

  let target = 'browser';

  // test incoming url for deeplink
  // if deeplink, modify insterstitial/click and call to click tracking
  const deeplinkRegex = /^(?!https?:\/\/|\/\/).*:\/\//;
  if (deeplinkRegex.test(localUrl)) {
    console.log('SDK called willSendRequestWithURL() with deeplink', localUrl);
    target = 'deeplink';
    // if deeplink, call to track link
    if (typeof window.callToTrackLink === 'function') {
      window.callToTrackLink();
    }
  }
  // encode all equals signs
  const encodedUrl = encodeURIComponent(localUrl);

  window.server.notifySdk(`click?target=${target}&url=${encodedUrl}`);
  return false;
}

// Android back button calls this window.dismiss, so click the close button in the app
function dismiss(target) {
  console.log("window.dismiss() called with", target);

  // PERFORMANCE FIX: Track dismissal source immediately, defer logging
  window._dismissalSource = window._dismissalSource || 'SDK';
  markUnloadAttribution('SDK_DISMISS_CALLED', { os: detectOsForAttribution(), target: target || null });

  // Close UI immediately - don't wait for logging
  const el = document.getElementById('closeButton');
  if (el !== null) {
    el.click();
    window.server.notifySdk('dismissSuccess');
  } else {
    window.server.notifySdk('dismissFail');
  }

  // Defer logging to next tick to avoid blocking UI update
  setTimeout(() => {
    // perfTime is automatically added by window.server.log()

    // Log SDK → E2 communication
    if (window.server && window.server.foreachAd) {
      window.server.foreachAd(ad => {
        window.server.log({
          tag: 'SDK_CALLBACK',
          fn: 'window.dismiss',
          msg: 'SDK called window.dismiss()',
          sdkCallback: 'dismiss',
          target: target || null,
          inmarketParams: {
            m2m_impr_id: ad.impression_id,
          },
        });
      });
    }

    // Get impression_id from ads (Option B pattern)
    const ads = Payload.findAdsInPayload(state.serverPayload);
    if (ads.length > 0) {
      window.server.log({
        tag: 'E2_DISMISS_CALLED',
        fn: 'window.dismiss',
        msg: 'window.dismiss() called',
        inmarketParams: {
          m2m_impr_id: ads[0].impression_id,
        },
        target: target || null,
        source: window._dismissalSource,
        hasE2ShowBeenCalled: state.hasInitialized || false,
      });
    } else {
      window.server.log({
        tag: 'E2_DISMISS_CALLED',
        fn: 'window.dismiss',
        msg: 'window.dismiss() called',
        target: target || null,
        source: window._dismissalSource,
        hasE2ShowBeenCalled: state.hasInitialized || false,
      });
    }

    // Reset source after logging
    window._dismissalSource = null;
  }, 0);
};

function actionForCloseInitiated() {
  console.log("actionForCloseInitiated called");

  // PREVENT DUPLICATE CALLS: If already called, skip
  if (actionForCloseInitiatedCalled) {
    console.warn('actionForCloseInitiated - already called, skipping duplicate');
    return;
  }
  actionForCloseInitiatedCalled = true;

  // PERFORMANCE FIX: Set dismissal source immediately, defer logging
  window._dismissalSource = window._dismissalSource || 'SDK';
  markUnloadAttribution('SDK_ACTION_FOR_CLOSE_INITIATED', { os: detectOsForAttribution() });

  // Close UI immediately - don't wait for logging
  const el = document.getElementById('closeAdButton');
  if (el !== null) {
    el.click();
    console.log("closeAdButton clicked");
  } else {
    console.log("closeAdButton was not found");
  }

  // Defer logging to next tick to avoid blocking UI update
  setTimeout(() => {
    // perfTime is automatically added by window.server.log()

    // Log SDK → E2 communication
    if (window.server && window.server.foreachAd) {
      window.server.foreachAd(ad => {
        window.server.log({
          tag: 'SDK_CALLBACK',
          fn: 'window.actionForCloseInitiated',
          msg: 'SDK called window.actionForCloseInitiated()',
          sdkCallback: 'actionForCloseInitiated',
          inmarketParams: {
            m2m_impr_id: ad.impression_id,
          },
        });
      });
    }

    // This is ONLY called by SDK (iOS)
    // Get impression_id from ads (Option B pattern)
    const ads = Payload.findAdsInPayload(state.serverPayload);
    if (ads.length > 0) {
      window.server.log({
        tag: 'E2_ACTION_FOR_CLOSE_INITIATED',
        fn: 'window.actionForCloseInitiated',
        msg: 'window.actionForCloseInitiated() called - SDK initiated',
        inmarketParams: {
          m2m_impr_id: ads[0].impression_id,
        },
        source: 'SDK',
        hasE2ShowBeenCalled: state.hasInitialized || false,
      });
    } else {
      window.server.log({
        tag: 'E2_ACTION_FOR_CLOSE_INITIATED',
        fn: 'window.actionForCloseInitiated',
        msg: 'window.actionForCloseInitiated() called - SDK initiated',
        source: 'SDK',
        hasE2ShowBeenCalled: state.hasInitialized || false,
      });
    }
  }, 0);
};

const setupSdkFunctions = () => {
  // //////////////////////////////////////////////////////////////////////////////
  /**
   * Lifecycle hooks for the SDK to call.
   */
  Object.assign(window, {
    preload,
    prepareToShow,
    doHeartBeat,
    logRequestUrl,
    show,
    unload,
    willSendRequestWithURL,
    dismiss,
    actionForCloseInitiated,
  });
  // end hooks for the SDK
  // //////////////////////////////////////////////////////////////////////////////
};

export {
  setupSdkFunctions,
  dismiss,
  doHeartBeat,
  injectOmSdk,
  logRequestUrl,
  preload,
  prepareToShow,
  runUnload,
  unload,
  show,
  willSendRequestWithURL,
  actionForCloseInitiated,
};
